import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang, UI } from "../i18n";
import { byCategory, FICHE, IMG } from "../content";
import { useLenis, Reveal } from "../components/motion";
import { PageHero } from "../components/PageHero";
import { MiniSite, CourtLines, MonoTag, GhostText, Stamp } from "../components/motifs";
import { ContactSection } from "../components/Layout";
import { THEMES } from "../content";

// Overview: one full-bleed themed band per sport-study program, linking to its mini-site
export default function Programmes() {
  useLenis();
  const { t } = useLang();
  const programs = byCategory("programmes");
  return (
    <MiniSite theme={THEMES.base}>
      <PageHero
        code="TERMINAL 01"
        overline={t(UI.nav.programmes)}
        title={t({ fr: "Programmes Sport-Études", en: "Sport-Study Programs", es: "Programas Deporte-Estudio" })}
        subtitle={t({ fr: "Trois voies d'excellence, une même exigence. Chaque programme est un mini-club avec sa propre identité, ses couleurs et son projet de développement.", en: "Three paths to excellence, one standard. Each program is a mini-club with its own identity, colors and development project.", es: "Tres caminos hacia la excelencia, una misma exigencia. Cada programa es un mini-club con su identidad, colores y proyecto de desarrollo." })}
        image={IMG.courtAction}
      />
      {programs.map((p, i) => {
        const th = p.theme;
        return (
          <section key={p.slug} data-testid={`program-${p.slug}`}
            className="relative overflow-hidden py-20 md:py-28 grain border-t border-white/10"
            style={{ backgroundColor: th.bg }}>
            <GhostText text={`0${i + 1}`} className="text-[32vw] -top-[5vw] right-0" />
            <CourtLines className={`${i % 2 ? "-right-52" : "-left-52"} bottom-0 w-[560px] h-[560px]`} />
            <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              <Reveal className={`lg:col-span-5 ${i % 2 ? "lg:order-2" : ""}`}>
                <div className="relative overflow-hidden rounded-lg">
                  <img src={p.img} alt={p.name} className="w-full h-[360px] md:h-[440px] object-cover" />
                  <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${th.bg}cc, transparent 60%)` }} />
                  <Stamp top={p.city} bottom={p.code} className="absolute bottom-5 right-5" color={th.accent} />
                </div>
              </Reveal>
              <div className={`lg:col-span-7 ${i % 2 ? "lg:order-1" : ""}`}>
                <Reveal>
                  {p.logo && (
                    <img src={p.logo} alt={p.name} data-testid={`program-logo-${p.slug}`}
                      className="h-20 w-20 object-contain rounded-xl bg-white p-1.5 mb-6 shadow-xl" />
                  )}
                  <div className="flex flex-wrap items-center gap-3">
                    <MonoTag className="border border-white/25 rounded-full px-3 py-1.5 text-white/70">{p.code}</MonoTag>
                    <MonoTag style={{ color: th.accent }}>{t(p.tag)}</MonoTag>
                  </div>
                  <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.9] mt-5">{p.name}</h2>
                  <p className="font-body text-white/70 mt-6 max-w-2xl text-base md:text-lg">{t(p.desc)}</p>
                  <div className="flex flex-wrap gap-x-8 gap-y-2 mt-6 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-white/60">
                    <span>{t(FICHE.age)}: {t(p.ages)}</span>
                    <span>{t(FICHE.duration)}: {t(p.duration)}</span>
                    <span>{t(FICHE.price)}: {t(p.price || FICHE.onRequest)}</span>
                  </div>
                  <div className="h-[3px] w-24 mt-8" style={{ backgroundColor: th.accent }} />
                  <Link to={`/programmes/${p.slug}`} data-testid={`program-cta-${p.slug}`}
                    className="inline-flex items-center gap-2 mt-8 rounded-full px-7 py-3.5 font-heading font-semibold uppercase text-sm tracking-wide text-black hover:opacity-85 transition-opacity duration-300"
                    style={{ backgroundColor: th.accent }}>
                    {t(FICHE.seeFiche)} <ArrowUpRight size={18} />
                  </Link>
                </Reveal>
              </div>
            </div>
          </section>
        );
      })}
      <ContactSection programs={programs.map((p) => p.name)} />
    </MiniSite>
  );
}
